import { formatCurrency } from "@angular/common";
import { Component, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { AgGridAngular } from "ag-grid-angular";
import { ReportEmbeddedType, ReportEmbeddedTypeEx, ReportSource, ReportStandardType, ReportStandardTypeEx, ReportSubscriptionContent, ReportSubscriptionLevel, ReportSubscriptionLevelEx, ReportSubscriptionPrice, ReportSubscriptionPriceEx, ReportSubscriptionType, SubPeriodTypes } from "../../../../api/models";
import { ReportExportService, SubPeriodTypesService } from "../../../../api/services";
import { EditReportEmbeddedTypeDialogComponent, EditReportEmbeddedTypeDialogData } from "../../../../dialogs/admin/edit-report-embedded-type-dialog/edit-report-embedded-type-dialog.component";
import { EditReportSourceDialogComponent, EditReportSourceDialogData } from "../../../../dialogs/admin/edit-report-source-dialog/edit-report-source-dialog.component";
import { EditReportStandardTypeDialogComponent, EditReportStandardTypeDialogData } from "../../../../dialogs/admin/edit-report-standard-type-dialog/edit-report-standard-type-dialog.component";
import { EditReportSubscriptionLevelDialogComponent, EditReportSubscriptionLevelDialogData } from "../../../../dialogs/admin/edit-report-subscription-level-dialog/edit-report-subscription-level-dialog.component";
import { EditReportSubscriptionPriceDialogComponent, EditReportSubscriptionPriceDialogData } from "../../../../dialogs/admin/edit-report-subscription-price-dialog/edit-report-subscription-price-dialog.component";
import { EditReportSubscriptionTypeDialogComponent, EditReportSubscriptionTypeDialogData } from "../../../../dialogs/admin/edit-report-subscription-type-dialog/edit-report-subscription-type-dialog.component";
import { ConfirmDialogComponent } from "../../../../dialogs/confirm-dialog/confirm-dialog.component";
import { DialogResultType } from "../../../../dialogs/DialogResult";
import { MessageDialogComponent } from "../../../../dialogs/message-dialog/message-dialog.component";
import { Clipboard } from "@angular/cdk/clipboard";

@Component({
  selector: "admin-report-export-config",
  templateUrl: "./admin-report-export-config.component.html",
  styleUrls: ["./admin-report-export-config.component.css"]
})
export class AdminReportExportConfigComponent {

  // #region Report Source

  loadingReportSources: boolean;
  allReportSources: ReportSource[];
  @ViewChild("reportSourceDataGrid") reportSourceDataGrid: AgGridAngular;

  loadingDeletedReportSources: boolean;
  allDeletedReportSources: ReportSource[];
  @ViewChild("deletedReportSourceDataGrid") deletedReportSourceDataGrid: AgGridAngular;

  reportSourceColumnDefs = [
    {
      headerName: "ID",
      field: "reportSourceId",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Name",
      field: "name",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Description",
      field: "description",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "File ID",
      field: "fileId",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "File Name",
      field: "fileName",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Active",
      field: "active",
      sortable: true,
      filter: true,
      resizable: true
    }
  ]

  loadReportSources() {
    this.loadingReportSources = true;
    this.loadingDeletedReportSources = true;
    this.reportExportService
      .reportExportGetReportSources()
      .subscribe((result) => {
        this.allReportSources = result;
        this.loadingReportSources = false;
        this.reportSourceDataGrid?.api?.sizeColumnsToFit()
      });

    this.reportExportService
      .reportExportGetDeletedReportSources()
      .subscribe((result) => {
        this.allDeletedReportSources = result;
        this.loadingDeletedReportSources = false;
      });
  }

  addReportSource(): boolean {
    const params: EditReportSourceDialogData = {
      dialogTitle: "Create Report Source",
      reportSource: {
        reportSourceId: -1,
        name: "<New Report Source>",
        description: "",
        fileId: "",
        fileName: "",
        preFilterCompanyAvailable: false,
        preFilterIndustryAvailable: false,
        preFilterOccupationAvailable: false,
        preFilterRegionAvailable: false,
        preFilterTitleAvailable: false,
        active: true,
        isDeleted: false
      },
      podPages: []
    };

    const dialogRef = this.dialog.open(EditReportSourceDialogComponent, {
      disableClose: true,
      width: "750px",
      data: params
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.ResultType != null && result.ResultType == DialogResultType.Ok) {
        this.loadReportSources();
      }
    });

    return false;
  }

  editSelectedReportSource(): boolean {
    const selectedNodes = this.reportSourceDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must select a row first."
      );
    } else {
      this.editReportSource(selectedData[0]);
    }

    return false;
  }

  editReportSource(reportSource: ReportSource) {
    this.reportExportService.reportExportGetReportSourcePrintOnDemandPages(reportSource.reportSourceId).subscribe((result) => {
      const params: EditReportSourceDialogData = {
        dialogTitle: "Update Report Source",
        reportSource: {
          reportSourceId: reportSource.reportSourceId,
          name: reportSource.name,
          description: reportSource.description,
          fileId: reportSource.fileId,
          fileName: reportSource.fileName,
          preFilterCompanyAvailable: reportSource.preFilterCompanyAvailable,
          preFilterIndustryAvailable: reportSource.preFilterIndustryAvailable,
          preFilterOccupationAvailable: reportSource.preFilterOccupationAvailable,
          preFilterRegionAvailable: reportSource.preFilterRegionAvailable,
          preFilterTitleAvailable: reportSource.preFilterTitleAvailable,
          active: reportSource.active,
          isDeleted: reportSource.isDeleted
        },
        podPages: result
      };

      const dialogRef = this.dialog.open(EditReportSourceDialogComponent, {
        disableClose: true,
        width: "750px",
        data: params
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result != null && result.ResultType != null && result.ResultType == DialogResultType.Ok) {
          this.loadReportSources();
        }
      });
    });
  }

  deleteSelectedReportSource(): boolean {
    const selectedNodes = this.reportSourceDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must selected a row first."
      );
    } else {
      ConfirmDialogComponent.showDialog(
        this.dialog,
        "Confirm Delete Report Source",
        "Are you sure you want to delete this report source?  This is a soft delete and can be undone."
      ).subscribe((result) => {
        if (result.ResultType === DialogResultType.Ok) {
          this.reportExportService
            .reportExportDeleteReportSource(selectedData[0].reportSourceId)
            .subscribe((result) => {
              this.loadReportSources();
            },
            (error) => {
              MessageDialogComponent.showDialog(
                this.dialog,
                "Error",
                "Report Source is in use and cannot be deleted."
              );
            });
        }
      });
    }

    return false;
  }

  unDeleteSelectedReportSource(): boolean {
    const selectedNodes = this.deletedReportSourceDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must selected a row first."
      );
    } else {
      ConfirmDialogComponent.showDialog(
        this.dialog,
        "Confirm Un-Delete Report Source",
        "Are you sure you want to un-delete this report source?"
      ).subscribe((result) => {
        if (result.ResultType === DialogResultType.Ok) {
          this.reportExportService
            .reportExportUnDeleteReportSource(selectedData[0].reportSourceId)
            .subscribe((result) => {
                this.loadReportSources();
              },
              (error) => {
                MessageDialogComponent.showDialog(
                  this.dialog,
                  "Error",
                  "Report Source is in use and cannot be deleted."
                );
              });
        }
      });
    }

    return false;
  }

  // #endregion

  // #region Report Standard Type

  loadingReportStandardTypes: boolean;
  allReportStandardTypes: ReportStandardTypeEx[];
  @ViewChild("reportStandardTypeDataGrid") reportStandardTypeDataGrid: AgGridAngular;
  reportStandardTypeColumnDefs = [
    {
      headerName: "ID",
      field: "reportStandardTypeId",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Name",
      field: "name",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Report Source",
      field: "reportSourceName",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Filter Page",
      field: "filterPage",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Report Pages",
      field: "reportPages",
      sortable: true,
      filter: true,
      resizable: true
    }
  ]

  loadReportStandardTypes() {
    this.loadingReportStandardTypes = true;
    this.reportExportService
      .reportExportGetReportStandardTypes()
      .subscribe((result) => {
        this.allReportStandardTypes = result;
        this.loadingReportStandardTypes = false;
        this.reportStandardTypeDataGrid?.api?.sizeColumnsToFit()
      });
  }

  addReportStandardType(): boolean {
    const params: EditReportStandardTypeDialogData = {
      dialogTitle: "Create Report Standard Type",
      reportSources: this.allReportSources,
      reportStandardType: {
        reportStandardTypeId: -1,
        reportSourceId: -1,
        name: "<New Report Standard Type>",
        filterPage: "",
        reportPages: ""
      }
    };

    const dialogRef = this.dialog.open(EditReportStandardTypeDialogComponent, {
      disableClose: true,
      width: "500px",
      data: params
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.ResultType != null && result.ResultType == DialogResultType.Ok) {
        this.loadReportStandardTypes();
      }
    });

    return false;
  }

  editSelectedReportStandardType(): boolean {
    const selectedNodes = this.reportStandardTypeDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must select a row first."
      );
    } else {
      this.editReportStandardType(selectedData[0]);
    }

    return false;
  }

  editReportStandardType(reportStandardType: ReportStandardType) {
    const params: EditReportStandardTypeDialogData = {
      dialogTitle: "Update Report Standard Type",
      reportSources: this.allReportSources,
      reportStandardType: {
        reportStandardTypeId: reportStandardType.reportStandardTypeId,
        name: reportStandardType.name,
        reportSourceId: reportStandardType.reportSourceId,
        filterPage: reportStandardType.filterPage,
        reportPages: reportStandardType.reportPages
      }
    };

    const dialogRef = this.dialog.open(EditReportStandardTypeDialogComponent, {
      disableClose: true,
      width: "500px",
      data: params
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.ResultType != null && result.ResultType == DialogResultType.Ok) {
        this.loadReportStandardTypes();
      }
    });
  }

  deleteSelectedReportStandardType(): boolean {
    const selectedNodes = this.reportStandardTypeDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must selected a row first."
      );
    } else {
      ConfirmDialogComponent.showDialog(
        this.dialog,
        "Confirm Delete Report Standard Type",
        "Are you sure you want to delete this report standard type?  Those in use cannot be deleted."
      ).subscribe((result) => {
        if (result.ResultType === DialogResultType.Ok) {
          this.reportExportService
            .reportExportDeleteReportStandardType(selectedData[0].reportStandardTypeId)
            .subscribe((result) => {
              this.loadReportStandardTypes();
            },
              (error) => {
                MessageDialogComponent.showDialog(
                  this.dialog,
                  "Error",
                  "Report Source is in use and cannot be deleted."
                );
              });
        }
      });
    }

    return false;
  }

  // #endregion

  // #region Report Embedded Type

  loadingReportEmbeddedTypes: boolean;
  allReportEmbeddedTypes: ReportEmbeddedTypeEx[];
  @ViewChild("reportEmbeddedTypeDataGrid") reportEmbeddedTypeDataGrid: AgGridAngular;
  reportEmbeddedTypeColumnDefs = [
    {
      headerName: "ID",
      field: "reportEmbeddedTypeId",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Name",
      field: "name",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Report Source",
      field: "reportSourceName",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Filter Page",
      field: "filterPage",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Report Pages",
      field: "reportPages",
      sortable: true,
      filter: true,
      resizable: true
    }
  ]

  loadReportEmbeddedTypes() {
    this.loadingReportEmbeddedTypes = true;
    this.reportExportService
      .reportExportGetReportEmbeddedTypes()
      .subscribe((result) => {
        this.allReportEmbeddedTypes = result;
        this.loadingReportEmbeddedTypes = false;
        this.reportEmbeddedTypeDataGrid?.api?.sizeColumnsToFit()
      });
  }

  addReportEmbeddedType(): boolean {
    const params: EditReportEmbeddedTypeDialogData = {
      dialogTitle: "Create Widget Type",
      reportSources: this.allReportSources,
      reportEmbeddedType: {
        reportEmbeddedTypeId: -1,
        reportSourceId: -1,
        name: "<New Widget Type>",
        filterPage: "",
        reportPages: ""
      }
    };

    const dialogRef = this.dialog.open(EditReportEmbeddedTypeDialogComponent, {
      disableClose: true,
      width: "500px",
      data: params
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.ResultType != null && result.ResultType == DialogResultType.Ok) {
        this.loadReportEmbeddedTypes();
      }
    });

    return false;
  }

  editSelectedReportEmbeddedType(): boolean {
    const selectedNodes = this.reportEmbeddedTypeDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must select a row first."
      );
    } else {
      this.editReportEmbeddedType(selectedData[0]);
    }

    return false;
  }

  editReportEmbeddedType(reportEmbeddedType: ReportEmbeddedType) {
    const params: EditReportEmbeddedTypeDialogData = {
      dialogTitle: "Update Widget Type",
      reportSources: this.allReportSources,
      reportEmbeddedType: {
        reportEmbeddedTypeId: reportEmbeddedType.reportEmbeddedTypeId,
        name: reportEmbeddedType.name,
        reportSourceId: reportEmbeddedType.reportSourceId,
        filterPage: reportEmbeddedType.filterPage,
        reportPages: reportEmbeddedType.reportPages
      }
    };

    const dialogRef = this.dialog.open(EditReportEmbeddedTypeDialogComponent, {
      disableClose: true,
      width: "500px",
      data: params
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.ResultType != null && result.ResultType == DialogResultType.Ok) {
        this.loadReportEmbeddedTypes();
      }
    });
  }

  deleteSelectedReportEmbeddedType(): boolean {
    const selectedNodes = this.reportEmbeddedTypeDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must selected a row first."
      );
    } else {
      ConfirmDialogComponent.showDialog(
        this.dialog,
        "Confirm Delete Widget Type",
        "Are you sure you want to delete this widget type?  Those in use cannot be deleted."
      ).subscribe((result) => {
        if (result.ResultType === DialogResultType.Ok) {
          this.reportExportService
            .reportExportDeleteReportEmbeddedType(selectedData[0].reportEmbeddedTypeId)
            .subscribe((result) => {
              this.loadReportEmbeddedTypes();
            },
              (error) => {
                MessageDialogComponent.showDialog(
                  this.dialog,
                  "Error",
                  "Widget is in use and cannot be deleted."
                );
              });
        }
      });
    }

    return false;
  }

  // #endregion

  // #region Report Subscription Type

  loadingReportSubscriptionTypes: boolean;
  allReportSubscriptionTypes: ReportSubscriptionType[];
  @ViewChild("reportSubscriptionTypeDataGrid") reportSubscriptionTypeDataGrid: AgGridAngular;

  loadingDeletedReportSubscriptionTypes: boolean;
  allDeletedReportSubscriptionTypes: ReportSubscriptionType[];
  @ViewChild("deletedReportSubscriptionTypeDataGrid") deletedReportSubscriptionTypeDataGrid: AgGridAngular;

  reportSubscriptionTypeColumnDefs = [
    {
      headerName: "ID",
      field: "reportSubscriptionTypeId",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Name",
      field: "name",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Description",
      field: "description",
      sortable: true,
      filter: true,
      resizable: true
    }
  ]

  loadReportSubscriptionTypes() {
    this.loadingReportSubscriptionTypes = true;
    this.reportExportService
      .reportExportGetReportSubscriptionTypes()
      .subscribe((result) => {
        this.allReportSubscriptionTypes = result;
        this.loadingReportSubscriptionTypes = false;
        this.reportSubscriptionTypeDataGrid?.api?.sizeColumnsToFit()
      });

    this.loadingDeletedReportSubscriptionTypes = true;
    this.reportExportService
      .reportExportGetDeletedReportSubscriptionTypes()
      .subscribe((result) => {
        this.allDeletedReportSubscriptionTypes = result;
        this.loadingDeletedReportSubscriptionTypes = false;
        this.deletedReportSubscriptionTypeDataGrid?.api?.sizeColumnsToFit()
      });
  }

  addReportSubscriptionType(): boolean {
    const params: EditReportSubscriptionTypeDialogData = {
      isNew: true,
      dialogTitle: "Add Report Subscription Type",
      reportStandardTypes: this.allReportStandardTypes,
      reportEmbeddedTypes: this.allReportEmbeddedTypes,
      selectedReportStandardTypes: [],
      selectedReportEmbeddedTypes: [],
      reportSubscriptionType: {
        reportSubscriptionTypeId: -1,
        name: "<Report Subscription Name>",
        description: "",
        licenseHtml: "",
        isDeleted: false
      }
    }

    const dialogRef = this.dialog.open(EditReportSubscriptionTypeDialogComponent, {
      disableClose: true,
      width: "500px",
      data: params
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.ResultType != null && result.ResultType == DialogResultType.Ok) {
        this.reportExportService.reportExportCreateReportSubscriptionType({
          reportStandardTypeIds: result.ResultData.selectedReportStandardTypes.map(x => x.reportStandardTypeId),
          reportEmbeddedTypeIds: result.ResultData.selectedReportEmbeddedTypes.map(x => x.reportEmbeddedTypeId),
          name: result.ResultData.reportSubscriptionType.name,
          description: result.ResultData.reportSubscriptionType.description,
          licenseHtml: result.ResultData.reportSubscriptionType.licenseHtml
        }).subscribe((result) => {
          this.loadReportSubscriptionTypes();
          this.loadReportSubscriptionContents();
        }, (error) => {
          alert(error);
        })
      }
    });

    return false;
  }

  editSelectedReportSubscriptionType(): boolean {
    const selectedNodes = this.reportSubscriptionTypeDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must select a row first."
      );
    } else {
      this.editReportSubscriptionType(selectedData[0]);
    }

    return false;
  }

  editReportSubscriptionType(reportSubscriptionType: ReportSubscriptionType) {

    var standardTypeIds: number[] = this.allReportSubscriptionContents
      .filter(x => x.reportSubscriptionTypeId == reportSubscriptionType.reportSubscriptionTypeId && x.reportStandardTypeId != null)
      .map(x => x.reportStandardTypeId);

    var standardTypes: ReportStandardType[] = this.allReportStandardTypes.filter((x) => {
      return standardTypeIds.indexOf(x.reportStandardTypeId) >= 0;
    });

    var embeddedTypeIds: number[] = this.allReportSubscriptionContents
      .filter(x => x.reportSubscriptionTypeId == reportSubscriptionType.reportSubscriptionTypeId && x.reportEmbeddedTypeId != null)
      .map(x => x.reportEmbeddedTypeId);

    var embeddedTypes: ReportEmbeddedType[] = this.allReportEmbeddedTypes.filter((x) => {
      return embeddedTypeIds.indexOf(x.reportEmbeddedTypeId) >= 0;
    });

    const params: EditReportSubscriptionTypeDialogData = {
      isNew: false,
      dialogTitle: "Edit Report Subscription Type",
      reportStandardTypes: this.allReportStandardTypes,
      reportEmbeddedTypes: this.allReportEmbeddedTypes,
      selectedReportStandardTypes: standardTypes,
      selectedReportEmbeddedTypes: embeddedTypes,
      reportSubscriptionType: {
        reportSubscriptionTypeId: reportSubscriptionType.reportSubscriptionTypeId,
        name: reportSubscriptionType.name,
        description: reportSubscriptionType.description,
        licenseHtml: reportSubscriptionType.licenseHtml,
        isDeleted: reportSubscriptionType.isDeleted
      }
    }

    const dialogRef = this.dialog.open(EditReportSubscriptionTypeDialogComponent, {
      disableClose: true,
      width: "500px",
      data: params
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.ResultType != null && result.ResultType == DialogResultType.Ok) {
        this.reportExportService.reportExportUpdateReportSubscriptionType({
          reportSubscriptionTypeId: result.ResultData.reportSubscriptionType.reportSubscriptionTypeId,
          reportStandardTypeIds: result.ResultData.selectedReportStandardTypes.map(x => x.reportStandardTypeId),
          reportEmbeddedTypeIds: result.ResultData.selectedReportEmbeddedTypes.map(x => x.reportEmbeddedTypeId),
          name: result.ResultData.reportSubscriptionType.name,
          description: result.ResultData.reportSubscriptionType.description,
          licenseHtml: result.ResultData.reportSubscriptionType.licenseHtml
        }).subscribe((result) => {
          this.loadReportSubscriptionTypes();
          this.loadReportSubscriptionContents();
        }, (error) => {
          alert(error);
        });
      }
    });

    return false;
  }

  deleteSelectedReportSubscriptionType(): boolean {
    const selectedNodes = this.reportSubscriptionTypeDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must selected a row first."
      );
    } else {
      ConfirmDialogComponent.showDialog(
        this.dialog,
        "Confirm Delete Report Subscription Type",
        "Are you sure you want to delete this report subscription type?  This is a soft delete and can be undone."
      ).subscribe((result) => {
        if (result.ResultType === DialogResultType.Ok) {
          this.reportExportService
            .reportExportDeleteReportSubscriptionType(selectedData[0].reportSubscriptionTypeId)
            .subscribe((result) => {
              this.loadReportSubscriptionTypes();
              this.loadReportSubscriptionContents();
              this.loadReportSubscriptionLevels();
              this.loadReportSubscriptionPrices();
            }, (error) => {
              MessageDialogComponent.showDialog(
                this.dialog,
                "Error",
                "Report Subscription Type is in use and cannot be deleted."
              );
            });
        }
      });
    }

    return false;
  }

  unDeleteSelectedReportSubscriptionType(): boolean {
    const selectedNodes = this.deletedReportSubscriptionTypeDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must selected a row first."
      );
    } else {
      ConfirmDialogComponent.showDialog(
        this.dialog,
        "Confirm Delete Report Subscription Type",
        "Are you sure you want to un-delete this report subscription type?"
      ).subscribe((result) => {
        if (result.ResultType === DialogResultType.Ok) {
          this.reportExportService
            .reportExportUnDeleteReportSubscriptionType(selectedData[0].reportSubscriptionTypeId)
            .subscribe((result) => {
              this.loadReportSubscriptionTypes();
              this.loadReportSubscriptionContents();
              this.loadReportSubscriptionLevels();
              this.loadReportSubscriptionPrices();
            }, (error) => {
              MessageDialogComponent.showDialog(
                this.dialog,
                "Error",
                "Report Subscription Type is in use and cannot be deleted."
              );
            });
        }
      });
    }

    return false;
  }

  // #endregion

  // #region Report Subscription Content

  loadingReportSubscriptionContents: boolean;
  allReportSubscriptionContents: ReportSubscriptionContent[];
  @ViewChild("reportSubscriptionContentDataGrid") reportSubscriptionContentDataGrid: AgGridAngular;
  reportSubscriptionContentColumnDefs = [
    {
      headerName: "ID",
      field: "reportSubscriptionContentId",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Report Subscription Type ID",
      field: "reportSubscriptionTypeId",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Report Standard Type ID",
      field: "reportStandardTypeId",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Report Embedded Type ID",
      field: "reportEmbeddedTypeId",
      sortable: true,
      filter: true,
      resizable: true
    }
  ]

  loadReportSubscriptionContents() {
    this.loadingReportSubscriptionContents = true;
    this.reportExportService
      .reportExportGetReportSubscriptionContents()
      .subscribe((result) => {
        this.allReportSubscriptionContents = result;
        this.loadingReportSubscriptionContents = false;
        this.reportSubscriptionContentDataGrid?.api?.sizeColumnsToFit()
      });
  }

  // #endregion

  // #region Report Subscription Level

  loadingReportSubscriptionLevels: boolean;
  allReportSubscriptionLevels: ReportSubscriptionLevelEx[];
  @ViewChild("reportSubscriptionLevelDataGrid") reportSubscriptionLevelDataGrid: AgGridAngular;
  reportSubscriptionLevelColumnDefs = [
    {
      headerName: "ID",
      field: "reportSubscriptionLevelId",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Report Subscription Type",
      field: "reportSubscriptionTypeName",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Allow Invoice",
      field: "allowInvoice",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Description",
      field: "description",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Max Admin Users",
      field: "maxAdminUsers",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Max Report Users",
      field: "maxReportUsers",
      sortable: true,
      filter: true,
      resizable: true
    }
  ]

  loadReportSubscriptionLevels() {
    this.loadingReportSubscriptionLevels = true;
    this.reportExportService
      .reportExportGetReportSubscriptionLevels()
      .subscribe((result) => {
        this.allReportSubscriptionLevels = result;
        this.loadingReportSubscriptionLevels = false;
        this.reportSubscriptionLevelDataGrid?.api?.sizeColumnsToFit()
      });
  }

  addReportSubscriptionLevel(): boolean {
    const params: EditReportSubscriptionLevelDialogData = {
      isNew: true,
      dialogTitle: "Create Report Subscription Level",
      reportSubscriptionTypes: this.allReportSubscriptionTypes,
      reportSubscriptionLevel: {
        reportSubscriptionLevelId: -1,
        reportSubscriptionTypeId: -1,
        allowInvoice: false,
        description: "",
        maxAdminUsers: 0,
        maxReportUsers: 0,
        allowPrintOnDemand: false
      }
    };

    const dialogRef = this.dialog.open(EditReportSubscriptionLevelDialogComponent, {
      disableClose: true,
      width: "500px",
      data: params
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.ResultType != null && result.ResultType == DialogResultType.Ok) {
        this.reportExportService.reportExportCreateReportSubscriptionLevel({
          reportSubscriptionTypeId: result.ResultData.reportSubscriptionLevel.reportSubscriptionTypeId,
          allowInvoice: result.ResultData.reportSubscriptionLevel.allowInvoice,
          description: result.ResultData.reportSubscriptionLevel.description,
          maxAdminUsers: result.ResultData.reportSubscriptionLevel.maxAdminUsers,
          maxReportUsers: result.ResultData.reportSubscriptionLevel.maxReportUsers,
          allowPrintOnDemand: result.ResultData.reportSubscriptionLevel.allowPrintOnDemand
        }).subscribe((result) => {
          this.loadReportSubscriptionLevels();
        }, (error) => {
          alert(error);
        })
      }
    });

    return false;
  }

  editSelectedReportSubscriptionLevel(): boolean {
    const selectedNodes = this.reportSubscriptionLevelDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must select a row first."
      );
    } else {
      this.editReportSubscriptionLevel(selectedData[0]);
    }

    return false;
  }

  editReportSubscriptionLevel(reportSubscriptionLevel: ReportSubscriptionLevel) {
    const params: EditReportSubscriptionLevelDialogData = {
      isNew: false,
      dialogTitle: "Edit Report Subscription Level",
      reportSubscriptionLevel: reportSubscriptionLevel,
      reportSubscriptionTypes: this.allReportSubscriptionTypes
    };

    const dialogRef = this.dialog.open(EditReportSubscriptionLevelDialogComponent, {
      disableClose: true,
      width: "500px",
      data: params
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.ResultType != null && result.ResultType == DialogResultType.Ok) {
        this.reportExportService.reportExportUpdateReportSubscriptionLevel({
          reportSubscriptionLevelId: result.ResultData.reportSubscriptionLevel.reportSubscriptionLevelId,
          reportSubscriptionTypeId: result.ResultData.reportSubscriptionLevel.reportSubscriptionTypeId,
          allowInvoice: result.ResultData.reportSubscriptionLevel.allowInvoice,
          description: result.ResultData.reportSubscriptionLevel.description,
          maxAdminUsers: result.ResultData.reportSubscriptionLevel.maxAdminUsers,
          maxReportUsers: result.ResultData.reportSubscriptionLevel.maxReportUsers,
          allowPrintOnDemand: result.ResultData.reportSubscriptionLevel.allowPrintOnDemand
        }).subscribe((result) => {
          this.loadReportSubscriptionLevels();
        }, (error) => {
          alert(error);
        })
      }
    })

    return false;
  }

  deleteSelectedReportSubscriptionLevel(): boolean {
    const selectedNodes = this.reportSubscriptionLevelDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must selected a row first."
      );
    } else {
      ConfirmDialogComponent.showDialog(
        this.dialog,
        "Confirm Delete Report Subscription Type",
        "Are you sure you want to delete this report subscription type?  Those in use cannot be deleted."
      ).subscribe((result) => {
        this.reportExportService
          .reportExportDeleteReportSubscriptionLevel({
            reportSubscriptionTypeId: selectedData[0].reportSubscriptionTypeId,
            reportSubscriptionLevelId: selectedData[0].reportSubscriptionLevelId
          }).subscribe((result) => {

          }, (error) => {
            MessageDialogComponent.showDialog(
              this.dialog,
              "Error",
              "Report Subscription Level is in use and cannot be deleted."
            );
          });
      })
    }

    return false;
  }

  // #endregion

  // #region Report Subscription Price

  loadingReportSubscriptionPrices: boolean;
  allReportSubscriptionPrices: ReportSubscriptionPriceEx[];
  @ViewChild("reportSubscriptionPriceDataGrid") reportSubscriptionPriceDataGrid: AgGridAngular;
  reportSubscriptionPriceColumnDefs = [
    {
      headerName: "Report Subscription Type",
      field: "reportSubscriptionTypeName",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Report Subscription Level",
      field: "reportSubscriptionLevelName",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Period Type",
      field: "periodTypeName",
      sortable: true,
      filter: true,
      resizable: true
    },
    {
      headerName: "Price",
      field: "price",
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: this.currencyValueFormatter,
    }
  ]

  loadReportSubscriptionPrices() {
    this.loadingReportSubscriptionPrices = true;
    this.reportExportService
      .reportExportGetReportSubscriptionPrices()
      .subscribe((result) => {
        this.allReportSubscriptionPrices = result;
        this.loadingReportSubscriptionPrices = false;
        this.reportSubscriptionPriceDataGrid?.api?.sizeColumnsToFit()
      });
  }

  addReportSubscriptionPrice(): boolean {
    const params: EditReportSubscriptionPriceDialogData = {
      isNew: true,
      dialogTitle: "Create Report Subscription Price",
      reportSubscriptionTypes: this.allReportSubscriptionTypes,
      reportSubscriptionLevels: this.allReportSubscriptionLevels,
      subscriptionPeriodTypes: this.allSubPeriodTypes,
      proposalFile: null,
      clearProposal: false,
      reportSubscriptionPrice: {
        reportSubscriptionTypeId: -1,
        reportSubscriptionLevelId: -1,
        periodTypeId: -1,
        price: null,
        customTerms: null,
        taxRate: null,
        proposalId: null,
        suppressInvoice: false
      }
    };

    const dialogRef = this.dialog.open(EditReportSubscriptionPriceDialogComponent, {
      disableClose: true,
      width: "500px",
      data: params
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.ResultType != null && result.ResultType == DialogResultType.Ok) {
        this.loadReportSubscriptionPrices();
      }
    })

    return false;
  }

  editSelectedReportSubscriptionPrice(): boolean {
    const selectedNodes = this.reportSubscriptionPriceDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must select a row first."
      );
    } else {
      this.editReportSubscriptionPrice(selectedData[0]);
    }

    return false;
  }

  editReportSubscriptionPrice(reportSubscriptionPrice: ReportSubscriptionPrice) {
    const params: EditReportSubscriptionPriceDialogData = {
      isNew: false,
      dialogTitle: "Update Report Subscription Price",
      reportSubscriptionTypes: this.allReportSubscriptionTypes,
      reportSubscriptionLevels: this.allReportSubscriptionLevels,
      subscriptionPeriodTypes: this.allSubPeriodTypes,
      proposalFile: null,
      clearProposal: false,
      reportSubscriptionPrice: {
        reportSubscriptionTypeId: reportSubscriptionPrice.reportSubscriptionTypeId,
        reportSubscriptionLevelId: reportSubscriptionPrice.reportSubscriptionLevelId,
        periodTypeId: reportSubscriptionPrice.periodTypeId,
        price: reportSubscriptionPrice.price,
        customTerms: reportSubscriptionPrice.customTerms,
        taxRate: reportSubscriptionPrice.taxRate,
        proposalId: reportSubscriptionPrice.proposalId,
        suppressInvoice: reportSubscriptionPrice.suppressInvoice
      }
    };

    const dialogRef = this.dialog.open(EditReportSubscriptionPriceDialogComponent, {
      disableClose: true,
      width: "500px",
      data: params
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.ResultType != null && result.ResultType == DialogResultType.Ok) {
        this.loadReportSubscriptionPrices();
      }
    })

    return false;
  }

  deleteSelectedReportSubscriptionPrice(): boolean {
    const selectedNodes = this.reportSubscriptionPriceDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must selected a row first."
      );
    } else {
      ConfirmDialogComponent.showDialog(
        this.dialog,
        "Confirm Delete Report Price",
        "Are you sure you want to delete this report price?"
      ).subscribe((result) => {
        if (result.ResultType === DialogResultType.Ok) {
          this.reportExportService
            .reportExportDeleteReportSubscriptionPrice({
              reportSubscriptionTypeId: selectedData[0].reportSubscriptionTypeId,
              reportSubscriptionLevelId: selectedData[0].reportSubscriptionLevelId,
              periodTypeId: selectedData[0].periodTypeId
            })
            .subscribe((result) => {
              this.loadReportSubscriptionPrices();
            });
        }
      });
    }

    return false;
  }

  copyReportSubscriptionPricePurchaseUrl(): boolean {
    const selectedNodes = this.reportSubscriptionPriceDataGrid.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);
    if (selectedData.length < 1) {
      MessageDialogComponent.showDialog(
        this.dialog,
        "Error",
        "You must select a row first."
      );
    } else {
      this.clipboard.copy(selectedData[0].purchaseUrl);
      MessageDialogComponent.showDialog(
        this.dialog,
        "Success",
        "'" + selectedData[0].purchaseUrl + "' copied to clipboard."
      );
    }

    return false;
  }

  // #endregion

  // #region Subscription Period Types

  allSubPeriodTypes: SubPeriodTypes[];

  loadSubPeriodTypes() {
    this.subPeriodTypesService.subPeriodTypesGetSubPeriodTypesAll()
      .subscribe((result) => {
        this.allSubPeriodTypes = result;
      }, (error) => alert(error));
  }

  // #endregion

  // #region Format Helpers

  currencyValueFormatter(params) {
    if (params.value === null || params.value === undefined) return "";

    return formatCurrency(params.value, "en-US", "$");
  }

  noOp() { return false; }

  resizeGrid(grid: AgGridAngular) {
    const allColumnIds = [];
    grid.columnApi
      .getAllColumns()
      .forEach(function (column) {
        allColumnIds.push(column.getColId());
      });
    grid.columnApi.autoSizeColumns(
      allColumnIds,
      false
    );

    return false;
  }

  // #endregion

  constructor(
    private reportExportService: ReportExportService,
    private subPeriodTypesService: SubPeriodTypesService,
    private dialog: MatDialog,
    private clipboard: Clipboard,
  ) {
    this.loadingReportSources = true;
    this.loadingReportStandardTypes = true;
    this.loadingReportEmbeddedTypes = true;
    this.loadingReportSubscriptionTypes = true;
    this.loadingReportSubscriptionContents = true;
    this.loadingReportSubscriptionLevels = true;
    this.loadingReportSubscriptionPrices = true;

    this.loadReportSources();
    this.loadReportStandardTypes();
    this.loadReportEmbeddedTypes();
    this.loadReportSubscriptionTypes();
    this.loadReportSubscriptionContents();
    this.loadReportSubscriptionLevels();
    this.loadReportSubscriptionPrices();
    this.loadSubPeriodTypes();
  }

}
